import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import QuestionMarkIcon from "components/svg/icons/QuestionMarkIcon";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TableData from "components/ui/TableData";
import { themevals } from "theme/themevals";
import { getRoles, deleteRole } from "api/Roles";
import TrashIcon from "components/svg/icons/TrashIcon";
import { EllipsisWrapper } from "containers/Companies/styled";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import PlusIcon from "components/svg/icons/PlusIcon";
import WarningIcon from "components/svg/icons/WarningIcon";
import P from "components/typography/P";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

type Role = {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  actions: any;
  lastUpdateBy?: {
    firstName?: string;
    lastName?: string;
  };
};

export default function Roles() {
  
  const navigate = useNavigate();
  const toast = useToast();  
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const [refreshData, setRefreshData] = useState<boolean>(false);  
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteInfo, setDeleteInfo] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [warningModal, setWarningModal] = useState<any>(false);

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    void trackActivity({
      module: "roles",
      action: "view_roles",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user?.id, user?.company?.id]);

  const columnHelper = createColumnHelper<Role>();
  const columns = [
    columnHelper.accessor("title", {
      id: "title",
      cell: (info) => <Box sx={{color:'black', cursor:"pointer"}} onClick={() => navigate(`/roles/${info.row.original.id}`)}>{info.getValue()}</Box>,
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row: any) => row.description, {
      id: "description",
      cell: (info) => info.getValue(),
      header: () => <span>Description</span>,
    }),    
    columnHelper.accessor((row: any) => row.createdAt, {
      id: "createdAt",
      cell: (info) => moment(info.getValue()).format("MM/DD/YYYY"),
      header: () => <span>Date Created</span>,
    }),    
    columnHelper.accessor((row: any) => row.updatedAt, {
      id: "updatedAt",
      cell: (info) => moment(info.getValue()).format("MM/DD/YYYY"),
      header: () => <span>Date Updated</span>,
    }),    
    columnHelper.accessor((row: any) => row.lastUpdateBy, {
      id: "lastUpdateBy",
      cell: (info) => `${info?.getValue()?.firstName || ""} ${info?.getValue()?.lastName || ""}`,
      header: () => <span>Updated By</span>,
    }), 
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        if (info.row.original.title !== "Admin" && info.row.original.title !== "Staff") {
          return (
            <EllipsisWrapper>
              <DropDownMenu
                toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />}
                items={[
                  { label: "Edit", onClick: () => navigate(`/roles/${info.row.original.id}`) },
                  {
                    label: "Delete",
                    style: { color: themevals.colors.danger },
                    onClick: () => {
                      setDeleteInfo(info.row.original);
                      setShowDeleteModal(true);
                    },
                  },
                ]}
              />
            </EllipsisWrapper>
          );
        }
        // Return null or an empty fragment if the condition is not met
        return null;
      },
      header: () => <span></span>,
      meta: {
        style: { width: "10px" },
      },
    })
    
  ];    
  
  const cancelDeleteRole = async () => {
    setShowDeleteModal(false) 
    setConfirmSubmit(false);
    setWarningModal(false);
  }


  const confirmDelete = async (id: string) => { 
    setConfirmSubmit(true);
    const res = await deleteRole({ data: { id : id } });
    if(res?.success){ 
      setConfirmSubmit(false);
      setShowDeleteModal(false) 
      toast.add(res?.message ? res.message : "Role deleted","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      setShowDeleteModal(false) 
      setConfirmSubmit(false);
      if(res?.statusCode === 409){
        setWarningModal(
          <BasicModal 
            title="Cannot delete role!" 
            cancel_click={cancelDeleteRole} 
            cancel_text="Close" 
            size={"sm"}
            icon={<WarningIcon fill={"#818181"} height="30px"/>}  
          >
            <P lineHeight={'22px'}>There are users associated with this role.  Please change any users with this role before attempting to delete.</P>
          </BasicModal>);
      }else{
        toast.add(res?.message ? res.message : "Error deleting role.","var(--theme-ui-colors-danger)");
      }
    }  
  };  

  return (
    <>
      <Header></Header>      
      <Page title="Roles" subtitle="Manage user roles and permissions.">                  
        <TableData 
          title={"All Roles"}
          api={getRoles} 
          columns={columns} 
          refresh={refreshData}
          actions={[
          <BasicButton 
          key="1" 
          onClick={() => navigate("/roles/create")}
          $iconHeight="10px"
          ><PlusIcon fill="#fff"/>Add role
          </BasicButton>
        ]} 
        />
      </Page>   
      {(showDeleteModal && deleteInfo) && 
        <BasicModal title="Delete Role?" 
          cancel_click={() => setShowDeleteModal(false)}
          cancel_text="No, cancel" 
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
          actions={<BasicButton
            type="button"    
            scheme="danger"      
            onClick={() => confirmDelete(deleteInfo.id)} 
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            >
            {!confirmSubmit &&
              <TrashIcon fill={"#fff"}/>
             }
              
              {"Delete"}</BasicButton>
          }
          >
          <p>Are you sure you want to delete <strong style={{color:themevals.colors.black}}>{deleteInfo.title}</strong>?</p>
        </BasicModal>
      }
      {warningModal}
    </>
  );
}
